<template>
  <a-spin :spinning="spinning">
    <div style="display: flex; gap: 16px;">
      <!-- 统计卡片 -->
      <div v-for="(stat, index) in stats" :key="index" :span="6" :style="statStyle">
        <!-- 图标和内容并列 -->
        <div :style="iconStyle">
          <component :is="stat.icon" />
        </div>
        <!-- 统计内容 -->
        <div class="stat-content">
          <div class="stat-title">{{ stat.title }}</div>
          <div class="stat-value-row">
            <div class="stat-value">{{ formatNumber(stat.value) }}</div>
            <span class="stat-suffix">{{ stat.suffix }}</span>
          </div>
        </div>
      </div>
    </div>

    <div :style="pieCardStyle">
      <div style="display: flex; align-items: baseline; justify-content: space-between;">
        <span style="display: flex; align-items: center;">
          <span :style="pieCardIconStyle">
            <component :is="pieSelection === 'contributor' ? TeamOutlined : CodeOutlined" />
          </span>
          <span class="pies-and-table-card-title">
            {{ pieSelection === 'contributor' ? "贡献者构成占比" : "代码量构成占比" }}
          </span>
        </span>
        <a-radio-group v-model:value="pieSelection">
          <a-radio-button value="contributor">贡献者</a-radio-button>
          <a-radio-button value="code">代码量</a-radio-button>
        </a-radio-group>
      </div>
      <div class="pie-and-table-cards-flex">
        <div class="pie-and-table-card">
          <!-- left chart and data -->
          <div class="pie-title-container">
            <div class="pie-title">
              华为 vs 非华为
            </div>
          </div>
          <div style="height: 100%;">
            <div style="height: 230px;">
              <my-pie-chart :data="leftPieData[pieSelection]" :colors="huaweiNonHuaweiColors"
                :suffix="pieSelection === 'contributor' ? '人' : '行'" />
            </div>
            <div style="display: flex;">
              <div v-for="(data, index) in leftPieData[pieSelection]" :key="index" class="pie-data-box"
                :style="{ borderColor: huaweiNonHuaweiColors[index] }">
                <div class="pie-data-title" style="margin-bottom: 6px;">
                  <span class="pie-data-dot" :style="{ color: huaweiNonHuaweiColors[index] }">●</span>
                  {{ data.name }}
                </div>
                <span style="font-size: 20px; font-weight: 600; ">
                  {{ formatNumber(data.value) }}
                </span>
                <span class="pie-data-suffix">
                  {{ pieSelection === 'contributor' ? ' 人' : ' 行' }}
                </span>
                <div style="font-size: 12px; color: #666; margin-top: 5px; ">
                  占比 {{((data.value / leftPieData[pieSelection].reduce((sum, item) => sum + item.value, 0)) *
                    100).toFixed(2)}}%
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="pie-and-table-card" style="min-width: 60%;">
          <!-- right chart and data -->
          <div class="pie-title-container">
            <div class="pie-title">非华为构成</div>
            <my-dropdown-checkbox v-model:checkedList="nonHuaweiCheckedList" :options="rightPieOptions" />
          </div>
          <div style="display: flex; align-items: flex-start;">
            <div style="width: 320px;">
              <my-pie-chart :data="rightPieData[pieSelection]" :colors="rightPieColors"
                :dimmed-names="rightPieDimmedNames" :suffix="pieSelection === 'contributor' ? '人' : '行'" />
            </div>
            <div style="flex: 1; display: flex; flex-direction: column; gap: 10px; padding-right: 8px;">
              <div v-for="(data, index) in displayedRightPieData" :key="index" class="pie-data-box"
                @click="toggleNonHuawei(data.name)" :class="{ 'pie-data-box-clickable': data.name !== '合计' }"
                :style="getRightPieItemStyle(data)">
                <div class="pie-data-title">
                  <span class="pie-data-dot" :style="{
                    color: isDimmed(data.name) ?
                      toRgba(getNonHuaweiColor(data.name), dimOpacity)
                      : getNonHuaweiColor(data.name)
                  }">
                    ●
                  </span>
                  {{ data.name }}
                </div>
                <div style="text-align: right;">
                  <span style="font-size: 16px; font-weight: 600; ">
                    {{ formatNumber(data.value) }}
                  </span>
                  <span class="pie-data-suffix">
                    {{ pieSelection === 'contributor' ? ' 人' : ' 行' }}
                  </span>
                  <div style="font-size: 12px; margin-top: 2px; ">
                    占非华为 {{ ((data.value / rightPieTotalSum) * 100).toFixed(2) }}% | 占总体 {{ ((data.value /
                      leftPieTotalSum) * 100).toFixed(2) }}%
                  </div>
                </div>
              </div>
              <div class="pie-data-box pie-data-total-box">
                <div class="pie-data-title">
                  <span class="pie-data-dot" style="color: #6366F1; font-size: 18px; margin-right: 2px;">∑</span>
                  <span style="font-weight: 700;">合计</span>
                </div>
                <div style="text-align: right;">
                  <span style="font-size: 16px; font-weight: 600; ">
                    {{ formatNumber(rightPieDataSum) }}
                  </span>
                  <span class="pie-data-suffix">
                    {{ pieSelection === 'contributor' ? ' 人' : ' 行' }}
                  </span>
                  <div style="font-size: 12px; margin-top: 2px; ">
                    占非华为 {{ rightPieTotalSum ? ((rightPieDataSum / rightPieTotalSum) * 100).toFixed(2) :
                      '0.00' }}% | 占总体 {{ leftPieTotalSum ? ((rightPieDataSum / leftPieTotalSum) *
                      100).toFixed(2) : '0.00' }}%
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </a-spin>
</template>

<script setup>

import { computed, ref, watch, onMounted } from 'vue';
import axios from 'axios';
import { notification } from 'ant-design-vue';
import { TeamOutlined, CodeOutlined, EnvironmentOutlined } from '@ant-design/icons-vue';
import MyPieChart from '../components/MyPieChart.vue';
import MyDropdownCheckbox from '../components/MyDropdownCheckbox.vue';

// ----- Constants & lookups -----
const nonHuaweiOrder = ['HABO', 'DONATE', 'PARTNER', 'SCHOOL', 'OTHERS'];
const nonHuaweiNameMap = { HABO: '伙伴：哈勃', DONATE: '伙伴：捐赠单位', PARTNER: '伙伴：其他', SCHOOL: '高校', OTHERS: '其他' };
const huaweiNonHuaweiColors = ['#EF4444', '#3B82F6'];
const nonHuaweiColorMap = {
  '伙伴：哈勃': '#15803D',
  '伙伴：捐赠单位': '#16A34A',
  '伙伴：其他': '#22C55E',
  '高校': '#60A5FA',
  '其他': '#A78BFA'
};

// ----- Helpers -----
const formatNumber = (n) => {
  if (n === null || n === undefined) return '';
  const num = typeof n === 'number' ? n : Number(n);
  return Number.isNaN(num) ? n : num.toLocaleString();
};

const buildNonHuaweiContributor = (situations) => {
  // 依据固定顺序把后端枚举映射成饼图数据
  const contributorArr = [];
  for (const key of nonHuaweiOrder) {
    const item = situations.find((s) => s.remarks === key);
    if (item) contributorArr.push({ name: nonHuaweiNameMap[key], value: item.number });
  }
  return contributorArr;
};

const buildNonHuaweiCode = (situations) => {
  const codeArr = [];
  for (const key of nonHuaweiOrder) {
    const item = situations.find((s) => s.remarks === key);
    if (item) codeArr.push({ name: nonHuaweiNameMap[key], value: item.codeLines });
  }
  return codeArr;
};

const getSituationCodeLines = (situations, remark) => {
  const item = situations.find((s) => s.remarks === remark);
  return item?.codeLines ?? 0;
};

const getNonHuaweiColor = (name) => nonHuaweiColorMap[name] || '#A78BFA';

const dimOpacity = 0.1;
const toRgba = (color, alpha) => {
  if (!color) return color;
  if (!color.startsWith('#')) return color;
  const hex = color.slice(1);
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  if (full.length !== 6) return color;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// ----- State -----
const pieSelection = ref('contributor');
const spinning = ref(false);
const totalNum = ref(0);
const totalCodeLines = ref(0);
const organizationNum = ref(0);
const nonHuaweiCheckedList = ref([]);

// ----- Derived state -----
const stats = computed(() => [{
  title: '贡献者数量',
  value: totalNum.value,
  suffix: '人',
  icon: TeamOutlined
}, {
  title: '代码量',
  value: totalCodeLines.value,
  suffix: '行',
  icon: CodeOutlined
}, {
  title: '组织数量',
  value: organizationNum.value,
  suffix: '家',
  icon: EnvironmentOutlined
}]);
const leftPieData = ref({ contributor: [], code: [] });
const rightPieData = ref({ contributor: [], code: [] });
const rightPieColors = computed(() => (rightPieData.value[pieSelection.value] || []).map((item) =>
  getNonHuaweiColor(item.name)));
const rightPieOptions = computed(() => (rightPieData.value[pieSelection.value] || []).map((item) => item.name));
// 用于监听选项变化，避免深度比较开销
const rightPieOptionsKey = computed(() => rightPieOptions.value.join('|'));
const leftPieTotalSum = computed(() => {
  const data = leftPieData.value[pieSelection.value] || [];
  return data.reduce((sum, item) => sum + item.value, 0);
});
const rightPieTotalSum = computed(() => {
  const data = rightPieData.value[pieSelection.value] || [];
  return data.reduce((sum, item) => sum + item.value, 0);
});
const displayedRightPieData = computed(() => rightPieData.value[pieSelection.value] || []);
const rightPieDataSum = computed(() => {
  return displayedRightPieData.value
    .filter((item) => !isDimmed(item.name))
    .reduce((sum, item) => sum + item.value, 0);
});
const rightPieDimmedNames = computed(() => {
  const selectedSet = new Set(nonHuaweiCheckedList.value);
  return (rightPieData.value[pieSelection.value] || [])
    .filter((item) => !selectedSet.has(item.name))
    .map((item) => item.name);
});

const isDimmed = (name) => rightPieDimmedNames.value.includes(name);

const toggleNonHuawei = (name) => {
  if (name === '合计') return;
  const current = new Set(nonHuaweiCheckedList.value);
  if (current.has(name)) {
    current.delete(name);
  } else {
    current.add(name);
  }
  nonHuaweiCheckedList.value = Array.from(current);
};

const getRightPieItemStyle = (item) => {
  const dimmed = isDimmed(item.name);
  const color = getNonHuaweiColor(item.name);
  return {
    borderColor: dimmed ? toRgba(color, dimOpacity) : color,
    backgroundColor: dimmed ? 'transparent' : '#FFFFFF',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '7px 16px',
    color: dimmed ? '#9CA3AF' : '#111827'
  };
};

// ----- API fetchers -----
const fetchData = async () => {
  // 上半区：概览 + TOP 贡献者
  const res = await axios.get('/metric/getSouthBoundSituation');
  if (res.data.code === 200) {
    const data = res.data.data;
    totalNum.value = data.totalNum;
    totalCodeLines.value = data.totalCodeLines;
    organizationNum.value = data.organizationNum;
    leftPieData.value = {
      contributor: [
        { name: '华为', value: data.huaweiNum },
        { name: '非华为', value: data.nonHuaweiNum }
      ],
      code: [
        { name: '华为', value: getSituationCodeLines(data.situations || [], 'HUAWEI') },
        { name: '非华为', value: (data.totalCodeLines || 0) - getSituationCodeLines(data.situations || [], 'HUAWEI') }
      ]
    };
    rightPieData.value = {
      contributor: buildNonHuaweiContributor(data.situations || []),
      code: buildNonHuaweiCode(data.situations || [])
    };
  } else {
    throw new Error(res.data?.msg || '请求失败');
  }
};

const loadData = async () => {
  spinning.value = true;
  try {
    await fetchData();
  } catch (error) {
    notification.error({ message: '数据加载失败', description: error?.message || error });
  } finally {
    spinning.value = false;
  }
};

// ----- Watchers & lifecycle -----

watch(
  rightPieOptionsKey,
  () => {
    // 数据源变化时，保持勾选与选项一致
    const options = rightPieOptions.value;
    nonHuaweiCheckedList.value = [...options];
  },
  { immediate: true }
);

onMounted(async () => {
  await loadData();
});

// ----- Styles -----

const currentColor = '#50A2FF'

const statStyle = computed(() => ({
  border: `2px solid ${currentColor}`,
  backgroundColor: '#fff',
  padding: '18px',
  borderRadius: '20px',
  transition: 'border-color 0.15s',
  minHeight: '110px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%'
}));

const iconStyle = computed(() => ({
  width: '56px',
  height: '56px',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: currentColor + '14',
  color: currentColor,
  fontSize: '22px',
  marginRight: '12px'
}));

const pieCardIconStyle = computed(() => ({
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: currentColor + '14',
  color: currentColor,
  fontSize: '16px',
  marginRight: '8px'
}));

const pieCardStyle = computed(() => ({
  border: `2px solid ${currentColor}`,
  backgroundColor: '#fff',
  padding: '24px 24px 0 24px',
  borderRadius: '20px',
  transition: 'border-color 0.15s',
  margin: '24px 0 24px 0'
}));
</script>

<style scoped>
.stat-content {
  flex: 1;
  text-align: center;
}

.stat-title {
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
  margin-bottom: 6px;
}

.stat-value-row {
  display: flex;
  align-items: baseline;
  justify-content: center;
}

.stat-value {
  font-size: 28px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  line-height: 1;
}

.stat-suffix {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-left: 6px;
}

.pies-and-table-card-title {
  font-size: 16px;
  font-weight: 600;
}

.pie-and-table-card {
  flex: 1;
  margin-top: 16px;
  margin-bottom: 24px;
  padding: 24px 16px 24px 16px;
  background-color: #f9fafb;
  border-radius: 12px;
}

.pie-and-table-cards-flex {
  display: flex;
  gap: 24px;
}

.pie-data-box {
  background-color: #fff;
  border: 2px solid;
  border-radius: 12px;
  padding: 12px 16px;
  width: 100%;
  flex: 1;
  margin: 0 8px;
  background-clip: padding-box;
  align-items: center;
  text-align: left;
}

.pie-data-title {
  font-size: 14px;
  display: flex;
  align-items: baseline;
  font-weight: 500;
}

.pie-data-dot {
  font-weight: 600;
  font-family: 'MS Mincho', 'SimSun', 'serif';
  margin-right: 4px;
}

.pie-data-suffix {
  font-size: 12px;
  margin-top: 4px;
}

.pie-data-total-box {
  border-color: #95A5F5;
  background-color: #EFF6FF;
  border-width: 2px;
  color: #4338CA;
  display: flex;
  justify-content: space-between;
  padding: 7px 16px;
  box-shadow: 0 0 12px 2px #95A5F533;
}

.pie-title-container {
  margin-bottom: 16px;
  padding-left: 16px;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}


.pie-data-box-clickable {
  cursor: pointer;
}

.pie-title {
  margin-bottom: 16px;
  font-size: 16px;
  font-weight: 600;
}

.caption-and-select-container {
  text-align: left;
  margin-right: 24px;
  margin-bottom: 16px;
}

.caption {
  margin-right: 8px;
  font-weight: 500;
  font-size: 14px;
  color: #333333;
}

.select {
  width: 120px;
}
</style>
